import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, KeyRound } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (password !== confirm) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/admin/reset-password`, { token, new_password: password });
      toast.success('Password reset successfully! Please log in.');
      setTimeout(() => navigate('/admin/login'), 1200);
    } catch (error) {
      console.error('Reset password error:', error);
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 px-4">
      <div className="w-full max-w-md p-8 bg-white border-2 border-black" data-testid="reset-password-form">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'var(--heading-font)' }}>
            CREATE NEW PASSWORD
          </h1>
          <p className="text-zinc-600">Enter a new password for your admin account</p>
        </div>

        {!token ? (
          <div className="bg-red-50 border border-red-200 p-4 text-sm text-red-800 text-center" data-testid="reset-no-token">
            Invalid or missing reset link. Please request a new password reset.
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="password">New Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="At least 8 characters"
                data-testid="new-password-input"
              />
            </div>
            <div>
              <Label htmlFor="confirm">Confirm Password</Label>
              <Input
                id="confirm"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                data-testid="confirm-password-input"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[var(--accent)] text-black hover:bg-[var(--accent-hover)] rounded-full px-8 py-4 text-lg font-bold transition-transform hover:scale-105 active:scale-95 flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="reset-password-btn"
            >
              <KeyRound size={20} />
              {loading ? 'RESETTING...' : 'RESET PASSWORD'}
            </button>
          </form>
        )}

        <div className="mt-8 pt-6 border-t border-zinc-200 text-center">
          <Link to="/admin/login" className="inline-flex items-center gap-2 text-sm font-medium text-zinc-600 hover:text-black transition-colors" data-testid="back-to-login-link">
            <ArrowLeft size={16} /> Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
